import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: API_KEY ? { 'x-api-key': API_KEY } : {},
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses (redirect to login)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth
export async function login(username, password) {
  const { data } = await api.post('/auth/login', { username, password });
  return data;
}

export async function loginWithGoogle(credential) {
  const { data } = await api.post('/auth/google', { credential });
  return data;
}

export async function getMe() {
  const { data } = await api.get('/auth/me');
  return data;
}

// Requests
export async function submitRequest(payload) {
  const { data } = await api.post('/requests', payload);
  return data;
}

export async function getRequests(page = 1, limit = 20, state = '', mode = '') {
  const params = { page, limit };
  if (state) params.state = state;
  if (mode) params.mode = mode;
  const { data } = await api.get('/requests', { params });
  return data;
}

export async function getRequestDetail(requestId) {
  const { data } = await api.get(`/requests/${requestId}`);
  return data;
}

export async function getStats() {
  const { data } = await api.get('/requests/stats');
  return data;
}

export async function getRequestResult(requestId) {
  const { data } = await api.get(`/requests/${requestId}/result`);
  return data;
}

export async function reprocessRequest(requestId, expenditureIds) {
  const body = expenditureIds ? { expenditure_ids: expenditureIds } : {};
  const { data } = await api.post(`/requests/${requestId}/reprocess`, body);
  return data;
}

export async function deleteRequest(requestId) {
  const { data } = await api.delete(`/requests/${requestId}`);
  return data;
}

export async function testPrompt(payload) {
  const { data } = await api.post('/requests/test-prompt', payload);
  return data;
}

// Batches
export async function getBatches(page = 1, limit = 20, status = '') {
  const params = { page, limit };
  if (status) params.status = status;
  const { data } = await api.get('/batches', { params });
  return data;
}

export async function getBatchStatus(batchId) {
  const { data } = await api.get(`/batches/${batchId}/status`);
  return data;
}

export async function getBatchResults(batchId) {
  const { data } = await api.get(`/batches/${batchId}/results`);
  return data;
}

export async function getBatchReceived(batchId) {
  const { data } = await api.get(`/batches/${batchId}/received`);
  return data;
}

export async function reprocessBatch(batchId) {
  const { data } = await api.post(`/batches/${batchId}/reprocess`);
  return data;
}

export async function reprocessBatchLine(batchId, expendRef) {
  const { data } = await api.post(`/batches/${batchId}/expenditures/${encodeURIComponent(expendRef)}/reprocess`);
  return data;
}

export async function resendAllBatchLines(batchId) {
  const { data } = await api.post(`/batches/${batchId}/resend-lines`);
  return data;
}

export async function pauseBatchApi(batchId) {
  const { data } = await api.post(`/batches/${batchId}/pause`);
  return data;
}

export async function stopBatchApi(batchId) {
  const { data } = await api.post(`/batches/${batchId}/stop`);
  return data;
}

export async function resumeBatchApi(batchId) {
  const { data } = await api.post(`/batches/${batchId}/resume`);
  return data;
}

export async function nudgeBatchApi(batchId) {
  const { data } = await api.post(`/batches/${batchId}/nudge`);
  return data;
}

// Award fund-authorization extraction (ORDS → /api/awards/extract)
export async function getExtractionJobs(page = 1, limit = 20, status = '', batchId = '') {
  const params = { page, limit };
  if (status)  params.status   = status;
  if (batchId) params.batch_id = batchId;
  const { data } = await api.get('/awards/extract', { params });
  return data;
}

export async function getExtractionJob(jobId, includeRows = false) {
  const { data } = await api.get(`/awards/extract/${encodeURIComponent(jobId)}`, { params: { include_rows: includeRows } });
  return data;
}

export async function submitExtraction(payload) {
  const { data } = await api.post('/awards/extract', payload);
  return data;
}

export async function reprocessExtractionJob(jobId) {
  const { data } = await api.post(`/awards/extract/${encodeURIComponent(jobId)}/reprocess`);
  return data;
}

export async function nudgeExtractionJob(jobId) {
  const { data } = await api.post(`/awards/extract/${encodeURIComponent(jobId)}/nudge`);
  return data;
}

export async function resendExtractionCallback(jobId) {
  const { data } = await api.post(`/awards/extract/${encodeURIComponent(jobId)}/resend`);
  return data;
}

export async function deleteExtractionJob(jobId) {
  const { data } = await api.delete(`/awards/extract/${encodeURIComponent(jobId)}`);
  return data;
}

export async function deleteBatch(batchId) {
  const { data } = await api.delete(`/batches/${batchId}`);
  return data;
}

// File upload
export async function uploadFile(file, fileType) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);
  const { data } = await api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function getUploadedFiles(type) {
  const params = type ? { type } : {};
  const { data } = await api.get('/upload', { params });
  return data;
}

export async function getFileAwards(fileId, search = '') {
  const params = search ? { search } : {};
  const { data } = await api.get(`/upload/${fileId}/awards`, { params });
  return data;
}

export async function deleteFile(fileId) {
  const { data } = await api.delete(`/upload/${fileId}`);
  return data;
}

// Create request from uploaded files
export async function createRequestFromFiles(payload) {
  const { data } = await api.post('/requests/from-files', payload);
  return data;
}

// Shared expenditure from a MatchRequest (no auth required when SHARE_PUBLIC=true)
export async function getSharedExpenditure(requestId, expId) {
  const { data } = await api.get(`/share/${requestId}/expenditure/${expId}`);
  return data;
}

// Shared expenditure from a Batch (no auth required when SHARE_PUBLIC=true)
export async function getSharedBatchExpenditure(batchId, expId) {
  const { data } = await api.get(`/share/batch/${batchId}/expenditure/${expId}`);
  return data;
}

// Shared all-lines view for a whole batch
export async function getSharedBatch(batchId) {
  const { data } = await api.get(`/share/batch/${batchId}`);
  return data;
}

// Shared all-expenditures view for a MatchRequest
export async function getSharedRequest(requestId) {
  const { data } = await api.get(`/share/${requestId}`);
  return data;
}

// Runtime configuration (no secrets)
export async function getConfig() {
  const { data } = await api.get('/config');
  return data;
}

// ─── Hybrid OS (OpenSearch) ──────────────────────────────────────────────────

// Synchronous match against OpenSearch (BM25 + kNN fusion)
export async function matchHybridOS(payload) {
  const { data } = await api.post('/match/hybrid-os', payload);
  return data;
}

// Synchronous match using the hybrid-os-judge consensus pipeline
// (OpenSearch retrieval + LLM scorer + LLM-as-judge)
export async function matchHybridOsJudge(payload) {
  const { data } = await api.post('/match/hybrid-os-judge', payload);
  return data;
}

// Generic synchronous match for any of the 6 modes
export async function matchByMode(mode, payload) {
  const { data } = await api.post(`/match/${encodeURIComponent(mode)}`, payload);
  return data;
}

// OpenSearch admin
export async function osHealth() {
  const { data } = await api.get('/match/os/health');
  return data;
}
export async function osEnsureIndex() {
  const { data } = await api.post('/match/os/ensure-index');
  return data;
}
export async function osIngest(candidates, opts = {}) {
  const { data } = await api.post('/match/os/ingest', { candidates, ...opts });
  return data;
}
export async function osDropIndex() {
  const { data } = await api.delete('/match/os/index');
  return data;
}
